import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, MapPin, MessageCircle, Loader2, LayoutGrid, Map as MapIcon } from "lucide-react";
import { toast } from "sonner";
import AppLayout, { PageHeader } from "@/components/app/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { marketApi, CONDITIONS, SORT_OPTIONS } from "@/lib/marketApi";
import ListingCard from "@/components/marketplace/ListingCard";
import ListingComposer from "@/components/marketplace/ListingComposer";
import MapView from "@/components/maps/MapView";
import { Link as RouterLink } from "react-router-dom";
import { fmtMoney } from "@/lib/format";

const TABS = [
  { id: "explore", label: "Explorar" },
  { id: "mine", label: "Mis anuncios", auth: true },
  { id: "favs", label: "Favoritos", auth: true },
];

export default function Marketplace() {
  const auth = useAuth();
  const isLogged = auth?.user && typeof auth.user === "object";
  const [tab, setTab] = useState("explore");
  const [view, setView] = useState("list"); // list | map
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [favsIds, setFavsIds] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);

  // filters
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [type, setType] = useState("");
  const [condition, setCondition] = useState("");
  const [sort, setSort] = useState("recent");
  const [nearMe, setNearMe] = useState(false);
  const [coords, setCoords] = useState(null);
  const [radiusKm, setRadiusKm] = useState(50);

  useEffect(() => { marketApi.categories().then(setCats); }, []);

  // Load items based on tab
  useEffect(() => {
    setLoading(true);
    const run = async () => {
      try {
        if (tab === "explore") {
          const params = { q, category: cat, type, condition, sort };
          if (nearMe && coords) {
            params.near_lat = coords.lat;
            params.near_lng = coords.lng;
            params.radius_km = radiusKm;
          }
          const data = await marketApi.list(params);
          setItems(data || []);
        } else if (tab === "mine" && isLogged) {
          const data = await marketApi.myListings(auth.token);
          setItems(data || []);
        } else if (tab === "favs" && isLogged) {
          const data = await marketApi.myFavorites(auth.token);
          setItems(data || []);
        }
      } catch {
        setItems([]);
      } finally { setLoading(false); }
    };
    run();
  }, [tab, q, cat, type, condition, sort, nearMe, coords, radiusKm, isLogged, auth?.token]);

  // Track favorited ids for the heart toggle
  useEffect(() => {
    if (!isLogged) { setFavsIds(new Set()); return; }
    marketApi.myFavorites(auth.token).then((favs) => {
      setFavsIds(new Set(favs.map((f) => f.id)));
    }).catch(() => {});
  }, [isLogged, auth?.token, items.length]); // eslint-disable-line

  const requestGeo = () => {
    if (!navigator.geolocation) { toast.error("Geolocalización no soportada"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setNearMe(true);
        toast.success("Ubicación capturada · cerca de mí activado");
      },
      (err) => toast.error(err.message || "No se pudo obtener ubicación"),
      { timeout: 8000 }
    );
  };

  const onToggleFav = async (lid) => {
    if (!isLogged) { toast.error("Inicia sesión para guardar"); return; }
    try {
      const r = await marketApi.toggleFavorite(auth.token, lid);
      setFavsIds((prev) => {
        const next = new Set(prev);
        if (r.favorited) next.add(lid); else next.delete(lid);
        return next;
      });
    } catch { toast.error("Error"); }
  };

  const catLabel = useMemo(() => {
    const m = {};
    cats.forEach((c) => { m[c.id] = c.label; });
    return m;
  }, [cats]);

  const visibleTabs = TABS.filter((t) => !t.auth || isLogged);

  return (
    <AppLayout hideRightRail>
      <PageHeader
        kicker="◊ Marketplace"
        title="Marketplace V2."
        subtitle="Productos y servicios entre personas. Geolocalizado · chat directo · multi-foto."
        actions={
          <div className="flex items-center gap-2">
            {isLogged && (
              <Link to="/marketplace/chat" data-testid="market-chat-link"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border hairline hover:border-coral transition">
                <MessageCircle className="w-3.5 h-3.5" /> Mis mensajes
              </Link>
            )}
            {isLogged && (
              <button data-testid="market-new-btn" onClick={() => setShowCreate(true)}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-coral text-white hover:bg-coral-hover">
                <Plus className="w-3.5 h-3.5" /> Publicar
              </button>
            )}
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap" data-testid="market-tabs">
        <div className="flex gap-2">
          {visibleTabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`market-tab-${t.id}`}
                    className={`text-xs px-3 py-1.5 rounded-full border hairline transition
                      ${tab === t.id ? "bg-ink text-cream border-ink" : "bg-cream-card hover:border-coral"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {/* View toggle */}
        <div className="inline-flex border hairline rounded-full bg-cream-card overflow-hidden text-xs">
          <button onClick={() => setView("list")} data-testid="market-view-list"
                  className={`px-3 py-1.5 inline-flex items-center gap-1.5 transition
                    ${view === "list" ? "bg-ink text-cream" : "hover:bg-cream"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Lista
          </button>
          <button onClick={() => setView("map")} data-testid="market-view-map"
                  className={`px-3 py-1.5 inline-flex items-center gap-1.5 transition
                    ${view === "map" ? "bg-ink text-cream" : "hover:bg-cream"}`}>
            <MapIcon className="w-3.5 h-3.5" /> Mapa
          </button>
        </div>
      </div>

      {/* Filters (only on explore tab) */}
      {tab === "explore" && (
        <div className="space-y-2 mb-4" data-testid="market-filters">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid" />
              <input data-testid="market-search" placeholder="Buscar productos o servicios…"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     className="w-full bg-cream-card border hairline rounded-full pl-10 pr-4 py-2 text-sm outline-none focus:border-coral" />
            </div>
            <select data-testid="market-category" value={cat} onChange={(e) => setCat(e.target.value)}
                    className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none">
              <option value="">Todas las categorías</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <select data-testid="market-type" value={type} onChange={(e) => setType(e.target.value)}
                    className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none">
              <option value="">Producto + Servicio</option>
              <option value="product">Productos</option>
              <option value="service">Servicios</option>
            </select>
            <select data-testid="market-condition" value={condition} onChange={(e) => setCondition(e.target.value)}
                    className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none">
              <option value="">Cualquier condición</option>
              {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <select data-testid="market-sort" value={sort} onChange={(e) => setSort(e.target.value)}
                    className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none">
              {SORT_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!nearMe ? (
              <button onClick={requestGeo} data-testid="market-near-me"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border hairline hover:border-coral transition">
                <MapPin className="w-3.5 h-3.5" /> Cerca de mí
              </button>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-coral text-white">
                  <MapPin className="w-3.5 h-3.5" /> Cerca de mí · {coords?.lat?.toFixed(2)}, {coords?.lng?.toFixed(2)}
                </span>
                <select value={radiusKm} onChange={(e) => setRadiusKm(Number(e.target.value))} data-testid="market-radius"
                        className="bg-cream-card border hairline rounded-full px-3 py-1.5 text-xs outline-none">
                  {[10, 25, 50, 100, 250, 500].map((r) => <option key={r} value={r}>{r} km</option>)}
                </select>
                <button onClick={() => { setNearMe(false); setCoords(null); }}
                        className="text-xs text-ink-mid hover:text-coral underline">limpiar</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Map view */}
      {view === "map" && (
        <MapView
          items={items}
          testid="market-map"
          height="70vh"
          renderPopup={(it) => (
            <div className="text-sm">
              {it.photos?.[0] && (
                <img src={it.photos[0]} alt={it.title} className="w-40 h-28 object-cover rounded-lg mb-2" />
              )}
              <p className="font-medium">{it.title}</p>
              <p className="text-coral font-serif text-lg">{fmtMoney(it.price_usd)}</p>
              {it.location && <p className="text-xs text-ink-mid">{it.location}</p>}
              <RouterLink to={`/marketplace/${it.id}`} className="text-xs text-coral hover:underline">
                Ver anuncio →
              </RouterLink>
            </div>
          )}
        />
      )}

      {/* Grid view */}
      {view === "list" && (
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="market-grid">
          {loading && (
            <p className="col-span-full text-center text-sm text-ink-mid py-12 inline-flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
            </p>
          )}
          {!loading && items.length === 0 && (
            <p className="col-span-full text-center text-sm text-ink-mid py-12" data-testid="market-empty">
              {tab === "mine" ? "Aún no has publicado ningún anuncio." :
               tab === "favs" ? "No tienes favoritos guardados." :
               "Sin resultados para esta búsqueda."}
            </p>
          )}
          {!loading && items.map((l) => (
            <ListingCard key={l.id} listing={l} categoryLabel={catLabel[l.category] || l.category}
                         onToggleFav={isLogged ? onToggleFav : null}
                         isFav={favsIds.has(l.id)} />
          ))}
        </section>
      )}

      {showCreate && (
        <ListingComposer cats={cats} onClose={() => setShowCreate(false)}
                         onCreated={(l) => { setShowCreate(false); setItems((arr) => [l, ...arr]); toast.success("Anuncio publicado"); }} />
      )}
    </AppLayout>
  );
}
